import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Checkbox,
  Breadcrumbs,
  Link,
  TextField,
  InputAdornment,
  Chip,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import {
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CloudUpload as UploadIcon,
  Search as SearchIcon,
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
  Storage as StorageIcon,
  Image as ImageIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import { assetsApi, AssetListItem, AssetBrowseResult, OCIMetadata } from '../services/assets-api';
import { ociRegistryApi, OCIAssetLink } from '../services/oci-registry-api';
import { s3BucketsApi, S3BucketConfig } from '../services/s3-buckets-api';
import { AssetUploadDialog } from './AssetUploadDialog';
import { OCILinkDialog } from './OCILinkDialog';
import {
  Sync as SyncIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Dns as RegistryIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';

const BUCKET_STORAGE_KEY = 'assetManager.selectedBucketId';

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '-';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i > 1 ? 1 : 0)} ${units[i]}`;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function getFileIcon(name: string) {
  const ext = name.split('.').pop()?.toLowerCase();
  if (ext === 'qcow2' || ext === 'iso' || ext === 'vmdk') return <ImageIcon fontSize="small" color="primary" />;
  if (ext === 'tgz' || ext === 'tar' || ext === 'gz' || ext === 'zip') return <ArchiveIcon fontSize="small" color="secondary" />;
  return <FileIcon fontSize="small" color="action" />;
}

function getAssetTypeChip(name: string) {
  const ext = name.split('.').pop()?.toLowerCase();
  if (ext === 'qcow2') return <Chip label="VM Image" size="small" color="primary" variant="outlined" />;
  if (ext === 'tgz') return <Chip label="Archive" size="small" color="secondary" variant="outlined" />;
  if (ext === 'tar') return <Chip label="Container" size="small" color="warning" variant="outlined" />;
  if (ext === 'gz') return <Chip label="Compressed" size="small" color="info" variant="outlined" />;
  if (ext === 'iso') return <Chip label="ISO" size="small" color="primary" variant="outlined" />;
  if (ext === 'sha256') return <Chip label="Checksum" size="small" variant="outlined" />;
  return null;
}

export const AssetManager: React.FC = () => {
  const [buckets, setBuckets] = useState<S3BucketConfig[]>([]);
  const [selectedBucketId, setSelectedBucketId] = useState<string>(() => {
    return localStorage.getItem(BUCKET_STORAGE_KEY) || '';
  });
  const [bucketsLoading, setBucketsLoading] = useState(true);
  const [currentPrefix, setCurrentPrefix] = useState('');
  const [browseResult, setBrowseResult] = useState<AssetBrowseResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [assetLinks, setAssetLinks] = useState<Record<string, OCIAssetLink[]>>({});
  const [assetMeta, setAssetMeta] = useState<Record<string, OCIMetadata>>({});
  const [linkDialogKey, setLinkDialogKey] = useState<string | null>(null);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [verifying, setVerifying] = useState<string | null>(null);
  const [verifyResults, setVerifyResults] = useState<Record<string, { match: boolean; actual: string; metaHash?: string | null; sidecarHash?: string | null }>>({});

  // Load available buckets on mount
  useEffect(() => {
    (async () => {
      try {
        const allBuckets = await s3BucketsApi.listBuckets();
        setBuckets(allBuckets);
        // If no saved selection or saved selection no longer valid, pick first bucket
        if (allBuckets.length > 0) {
          const savedId = localStorage.getItem(BUCKET_STORAGE_KEY) || '';
          const valid = allBuckets.some(b => b.id === savedId);
          if (!valid) {
            const defaultBucket = allBuckets.find(b => b.bucket === 'rangeos-terraform-content') || allBuckets[0];
            setSelectedBucketId(defaultBucket.id);
            localStorage.setItem(BUCKET_STORAGE_KEY, defaultBucket.id);
          }
        }
      } catch (err: any) {
        setError('Failed to load bucket list');
      } finally {
        setBucketsLoading(false);
      }
    })();
  }, []);

  const loadData = useCallback(async (prefix: string, continuationToken?: string) => {
    if (!selectedBucketId) return;

    if (continuationToken) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const result = await assetsApi.browse(selectedBucketId, prefix, continuationToken);

      if (continuationToken && browseResult) {
        setBrowseResult({
          ...result,
          folders: [...browseResult.folders, ...result.folders],
          files: [...browseResult.files, ...result.files],
        });
      } else {
        setBrowseResult(result);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load assets');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [browseResult, selectedBucketId]);

  const fetchLinksForFiles = useCallback(async (files: AssetListItem[]) => {
    if (!selectedBucketId) return;
    const containerFiles = files.filter(f => {
      const ext = f.name.split('.').pop()?.toLowerCase();
      return ext === 'tgz' || ext === 'tar' || ext === 'gz';
    });
    if (containerFiles.length === 0) {
      setAssetLinks({});
      setAssetMeta({});
      return;
    }
    const keys = containerFiles.map(f => f.key);
    const [linksResult, metaResult] = await Promise.all([
      ociRegistryApi.getLinksByS3Keys(keys).catch(() => ({} as Record<string, OCIAssetLink[]>)),
      assetsApi.getOCIMetadata(selectedBucketId, keys).catch(() => ({} as Record<string, OCIMetadata>)),
    ]);
    setAssetLinks(linksResult);
    setAssetMeta(metaResult);
  }, [selectedBucketId]);

  useEffect(() => {
    if (selectedBucketId) {
      loadData(currentPrefix);
    }
  }, [currentPrefix, selectedBucketId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (browseResult?.files) {
      fetchLinksForFiles(browseResult.files);
    }
  }, [browseResult?.files]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBucketChange = (newBucketId: string) => {
    setSelectedBucketId(newBucketId);
    localStorage.setItem(BUCKET_STORAGE_KEY, newBucketId);
    setCurrentPrefix('');
    setSelectedKeys(new Set());
    setFilter('');
    setBrowseResult(null);
  };

  const navigateTo = (prefix: string) => {
    setCurrentPrefix(prefix);
    setSelectedKeys(new Set());
    setFilter('');
  };

  const handleRefresh = () => {
    loadData(currentPrefix);
    setSelectedKeys(new Set());
  };

  const handleSyncAssetLink = async (link: OCIAssetLink) => {
    setSyncing(link.id);
    try {
      await ociRegistryApi.syncLink(link.id);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSyncing(null);
    }
  };

  const handleUnlinkAsset = async (link: OCIAssetLink) => {
    try {
      await ociRegistryApi.removeLink(link.id);
      setAssetLinks(prev => {
        const updated = { ...prev };
        const remaining = (updated[link.s3Key] || []).filter(l => l.id !== link.id);
        if (remaining.length === 0) delete updated[link.s3Key];
        else updated[link.s3Key] = remaining;
        return updated;
      });
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleVerifyChecksum = async (key: string) => {
    setVerifying(key);
    try {
      const result = await assetsApi.verifyChecksum(selectedBucketId, key);
      setVerifyResults(prev => ({ ...prev, [key]: result }));
      if (!result.match) {
        const filename = key.split('/').pop();
        const parts: string[] = [];
        if (result.metaMatch === false) parts.push(`.meta.json sha256 mismatch`);
        if (result.sidecarMatch === false) parts.push(`.sha256 sidecar mismatch`);
        if (!result.sidecarConsistent) parts.push(`.meta.json and .sha256 disagree`);
        setError(`Checksum verification failed for ${filename}: ${parts.join('; ')}. Actual: ${result.actual.slice(0, 16)}...`);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setVerifying(null);
    }
  };

  const handleDownload = (key: string) => {
    assetsApi.downloadFile(selectedBucketId, key);
  };

  const handleDeleteSelected = async () => {
    if (selectedKeys.size === 0) return;
    if (!confirm(`Delete ${selectedKeys.size} file(s)? This cannot be undone.`)) return;

    setDeleteLoading(true);
    try {
      await assetsApi.deleteFiles(selectedBucketId, Array.from(selectedKeys));
      setSelectedKeys(new Set());
      await loadData(currentPrefix);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to delete files');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteSingle = async (key: string) => {
    const name = key.split('/').pop();
    if (!confirm(`Delete "${name}"? This cannot be undone.`)) return;

    setDeleteLoading(true);
    try {
      await assetsApi.deleteFiles(selectedBucketId, [key]);
      await loadData(currentPrefix);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to delete file');
    } finally {
      setDeleteLoading(false);
    }
  };

  const toggleSelectAll = () => {
    if (!browseResult) return;
    const fileKeys = browseResult.files.map(f => f.key);
    if (selectedKeys.size === fileKeys.length) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(fileKeys));
    }
  };

  const toggleSelect = (key: string) => {
    const newSet = new Set(selectedKeys);
    if (newSet.has(key)) {
      newSet.delete(key);
    } else {
      newSet.add(key);
    }
    setSelectedKeys(newSet);
  };

  const breadcrumbParts = currentPrefix
    ? currentPrefix.replace(/\/$/, '').split('/')
    : [];

  const selectedBucket = buckets.find(b => b.id === selectedBucketId);

  // Filter items (hide .meta.json sidecar files)
  const folders = browseResult?.folders.filter(f =>
    !filter || f.name.toLowerCase().includes(filter.toLowerCase())
  ) || [];
  const files = browseResult?.files.filter(f =>
    !f.name.endsWith('.meta.json') &&
    (!filter || f.name.toLowerCase().includes(filter.toLowerCase()))
  ) || [];

  if (bucketsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (buckets.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">No S3 buckets configured. Add a bucket in Settings to browse assets.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with bucket selector */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <StorageIcon color="primary" />
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <Select
            value={selectedBucketId}
            onChange={(e) => handleBucketChange(e.target.value)}
            displayEmpty
            sx={{ fontWeight: 'bold' }}
          >
            {buckets.map(b => (
              <MenuItem key={b.id} value={b.id}>
                {b.name} <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, fontFamily: 'monospace', fontSize: '0.75rem' }}>s3://{b.bucket}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        {selectedBucket && (
          <Chip
            label={`s3://${selectedBucket.bucket}`}
            size="small"
            variant="outlined"
            sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
          />
        )}
      </Box>

      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 1 }}>
        <Link
          component="button"
          variant="body2"
          underline="hover"
          onClick={() => navigateTo('')}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
        >
          <HomeIcon fontSize="small" />
          Root
        </Link>
        {breadcrumbParts.map((part, index) => {
          const prefix = breadcrumbParts.slice(0, index + 1).join('/') + '/';
          const isLast = index === breadcrumbParts.length - 1;

          return isLast ? (
            <Typography key={prefix} variant="body2" color="text.primary" fontWeight="bold">
              {part}
            </Typography>
          ) : (
            <Link
              key={prefix}
              component="button"
              variant="body2"
              underline="hover"
              onClick={() => navigateTo(prefix)}
              sx={{ cursor: 'pointer' }}
            >
              {part}
            </Link>
          );
        })}
      </Breadcrumbs>

      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Tooltip title="Refresh">
          <IconButton onClick={handleRefresh} size="small" disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Button
          variant="contained"
          size="small"
          startIcon={<UploadIcon />}
          onClick={() => setUploadDialogOpen(true)}
        >
          Upload
        </Button>
        {selectedKeys.size > 0 && (
          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteSelected}
            disabled={deleteLoading}
          >
            Delete ({selectedKeys.size})
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          placeholder="Filter..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 250 }}
        />
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Table */}
      {!loading && browseResult && (
        <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: 40 }}>
                  {files.length > 0 && (
                    <Checkbox
                      size="small"
                      checked={selectedKeys.size === files.length && files.length > 0}
                      indeterminate={selectedKeys.size > 0 && selectedKeys.size < files.length}
                      onChange={toggleSelectAll}
                    />
                  )}
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell sx={{ width: 120 }}>Type</TableCell>
                <TableCell sx={{ width: 100 }} align="right">Size</TableCell>
                <TableCell sx={{ width: 160 }}>Last Modified</TableCell>
                <TableCell sx={{ width: 100 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Folders */}
              {folders.map((folder) => (
                <TableRow
                  key={folder.key}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigateTo(folder.key)}
                >
                  <TableCell padding="checkbox" />
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FolderIcon fontSize="small" color="warning" />
                      <Typography variant="body2" fontWeight="medium">
                        {folder.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label="Folder" size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell />
                </TableRow>
              ))}

              {/* Files */}
              {files.map((file) => {
                const fileLinks = assetLinks[file.key] || [];
                const fileMeta = assetMeta[file.key];
                const isContainerFile = /\.(tgz|tar|gz)$/i.test(file.name);
                return (
                <TableRow key={file.key} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedKeys.has(file.key)}
                      onChange={() => toggleSelect(file.key)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {getFileIcon(file.name)}
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {file.name}
                      </Typography>
                      {fileLinks.map(link => (
                        <Tooltip key={link.id} title={`${link.registryName}: ${link.repository}:${link.tag} (${link.platform})`}>
                          <Chip
                            icon={<LinkIcon />}
                            label={`${link.repository.split('/').pop()}:${link.tag}`}
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                          />
                        </Tooltip>
                      ))}
                      {fileLinks.length === 0 && fileMeta?.repository && (
                        <Tooltip title={`${fileMeta.registryName}: ${fileMeta.repository}:${fileMeta.tag} (${fileMeta.platform}) - pulled ${fileMeta.pulledAt ? new Date(fileMeta.pulledAt).toLocaleDateString() : 'unknown'}`}>
                          <Chip
                            icon={<RegistryIcon />}
                            label={`${fileMeta.repository.split('/').pop()}:${fileMeta.tag}`}
                            size="small"
                            color="default"
                            variant="outlined"
                            sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                          />
                        </Tooltip>
                      )}
                      {fileMeta?.sha256 && (() => {
                        const vr = verifyResults[file.key];
                        const isVerifying = verifying === file.key;
                        const chipColor = vr ? (vr.match ? 'success' : 'error') : 'default';
                        const source = fileMeta.source ? 'OCI pull' : '.sha256 sidecar';
                        const tooltipText = isVerifying
                          ? 'Verifying checksum against S3 file...'
                          : vr
                            ? (vr.match ? `Verified: ${vr.actual}` : `MISMATCH! File: ${vr.actual.slice(0, 20)}...`)
                            : `SHA-256 (${source}): ${fileMeta.sha256} — click to verify`;
                        return (
                          <Tooltip title={tooltipText}>
                            <Chip
                              icon={isVerifying ? <CircularProgress size={14} /> : <VerifiedIcon />}
                              label={`sha256:${fileMeta.sha256.slice(0, 12)}...`}
                              size="small"
                              color={chipColor as any}
                              variant="outlined"
                              clickable
                              onClick={() => !isVerifying && handleVerifyChecksum(file.key)}
                              sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                            />
                          </Tooltip>
                        );
                      })()}
                    </Box>
                  </TableCell>
                  <TableCell>{getAssetTypeChip(file.name)}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {formatFileSize(file.size)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                      {formatDate(file.lastModified)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      {fileLinks.length > 0 && (
                        <>
                          <Tooltip title="Sync (re-pull from registry)">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleSyncAssetLink(fileLinks[0])}
                              disabled={syncing === fileLinks[0].id}
                            >
                              {syncing === fileLinks[0].id ? <CircularProgress size={16} /> : <SyncIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Unlink from registry">
                            <IconButton
                              size="small"
                              onClick={() => handleUnlinkAsset(fileLinks[0])}
                            >
                              <LinkOffIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {fileLinks.length === 0 && isContainerFile && (
                        <Tooltip title="Link to Registry">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => setLinkDialogKey(file.key)}
                          >
                            <LinkIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Download">
                        <IconButton size="small" onClick={() => handleDownload(file.key)}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteSingle(file.key)}
                          disabled={deleteLoading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
                );
              })}

              {/* Empty state */}
              {folders.length === 0 && files.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {filter ? 'No items match the filter' : 'This prefix is empty'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Load more button */}
      {browseResult?.truncated && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => loadData(currentPrefix, browseResult.continuationToken)}
            disabled={loadingMore}
          >
            {loadingMore ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            Load More
          </Button>
        </Box>
      )}

      {/* Summary */}
      {browseResult && !loading && (
        <Box sx={{ display: 'flex', gap: 2, mt: 1, opacity: 0.7 }}>
          <Typography variant="caption">
            {folders.length} folder(s), {files.length} file(s)
          </Typography>
          {files.length > 0 && (
            <Typography variant="caption">
              Total: {formatFileSize(files.reduce((sum, f) => sum + f.size, 0))}
            </Typography>
          )}
        </Box>
      )}

      {/* Upload Dialog */}
      <AssetUploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        currentPrefix={currentPrefix}
        bucketId={selectedBucketId}
        onUploadComplete={handleRefresh}
      />

      {/* OCI Link Dialog */}
      {linkDialogKey && (
        <OCILinkDialog
          open={true}
          onClose={() => setLinkDialogKey(null)}
          s3Key={linkDialogKey}
          onLinkCreated={() => {
            if (browseResult?.files) fetchLinksForFiles(browseResult.files);
          }}
        />
      )}
    </Box>
  );
};
